/** @param {unknown} s */
function normalizeFingerprintPart(s) {
    return String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Display name fragment for grouping duplicate Plaid links (same issuer + mask). */
export function plaidAccountFingerprint(account) {
    if (!account) return '';
    const inst = normalizeFingerprintPart(account.institutionName);
    const mask = String(account.mask ?? '').trim();
    const rawName =
        account.officialName != null && String(account.officialName).trim() !== ''
            ? String(account.officialName).trim()
            : String(account.name ?? '').trim();
    const nameKey = normalizeFingerprintPart(rawName);
    const typeKey = normalizeFingerprintPart(account.type);
    return `${inst}|${mask}|${nameKey}|${typeKey}`;
}

/**
 * When the same physical account is linked twice (separate PfAccount rows / ids), keep one row
 * for menus and Settings. Sort key = lowest DB id as canonical {@link linkedAccountIds}[0].
 * @param {Array<Record<string, unknown>>} accounts — each must include `institutionName`, `id`, `name`.
 * @returns {Array<Record<string, unknown> & { linkedAccountIds: number[] }>}
 */
export function dedupePlaidLinkedAccounts(accounts) {
    if (!Array.isArray(accounts) || accounts.length === 0) return [];
    /** @type {Map<string, Array<Record<string, unknown>>>} */
    const groups = new Map();
    for (const a of accounts) {
        const fp = plaidAccountFingerprint(a);
        if (!groups.has(fp)) groups.set(fp, []);
        groups.get(fp).push(a);
    }
    const out = [];
    for (const [, grpRaw] of groups) {
        const grp = grpRaw.slice().sort((x, y) => Number(x.id) - Number(y.id));
        const canonical = grp[0];
        const linkedAccountIds = grp.map((g) => Number(g.id)).filter((id) => Number.isFinite(id));
        out.push({ ...canonical, linkedAccountIds });
    }
    return out.sort((a, b) =>
        plaidAccountMenuLabel(a).localeCompare(plaidAccountMenuLabel(b), undefined, { sensitivity: 'base' }),
    );
}

/**
 * User-defined label from Settings → Connections, else Plaid account name.
 * @param {{ displayName?: string | null, name?: string | null } | null | undefined} account
 */
export function plaidAccountMenuLabel(account) {
    if (!account) return '—';
    const nick = account.displayName != null && String(account.displayName).trim() !== ''
        ? String(account.displayName).trim()
        : '';
    if (nick) return nick;
    return account.name?.trim() || 'Account';
}

/**
 * Merge API nested `tx.account` with the enriched row from `/api/plaid/institutions` (e.g. `institutionName`).
 * @param {Record<string, unknown> | null | undefined} apiAccount
 * @param {Array<Record<string, unknown>>} allAccounts
 */
export function mergePlaidAccountFromList(apiAccount, allAccounts = []) {
    if (!apiAccount || !apiAccount.id || !Array.isArray(allAccounts)) return apiAccount;
    const sid = String(apiAccount.id);
    const found = allAccounts.find(
        (a) =>
            String(a.id) === sid
            || (Array.isArray(a.linkedAccountIds) && a.linkedAccountIds.some((lid) => String(lid) === sid)),
    );
    if (!found) return apiAccount;
    return { ...apiAccount, ...found };
}

/**
 * Label for account filter dropdown (and table) when multiple accounts share the same menu title.
 * If the title is unique among `allAccounts`, returns the same as {@link plaidAccountMenuLabel}.
 * @param {Record<string, unknown> | null | undefined} account
 * @param {Array<Record<string, unknown>>} [allAccounts]
 */
export function plaidAccountFilterLabel(account, allAccounts = []) {
    if (!account) return '—';
    const base = plaidAccountMenuLabel(account);
    const list = Array.isArray(allAccounts) ? allAccounts : [];
    const sameBase = list.filter((a) => plaidAccountMenuLabel(a) === base);
    if (sameBase.length <= 1) return base;

    const mask = account.mask != null ? String(account.mask).trim() : '';
    const inst = account.institutionName != null ? String(account.institutionName).trim() : '';
    const bits = [base];
    if (mask) bits.push(`•••• ${mask}`);

    const fingerprint = `${base}|${mask}|${inst}`;
    const sameFingerprint = list.filter((a) => {
        const m = a.mask != null ? String(a.mask).trim() : '';
        const i = a.institutionName != null ? String(a.institutionName).trim() : '';
        return `${plaidAccountMenuLabel(a)}|${m}|${i}` === fingerprint;
    });
    if (sameFingerprint.length > 1) {
        bits.push(`#${account.id}`);
    } else if (!mask && inst) {
        bits.push(inst);
    }
    return bits.join(' · ');
}
